import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Box,
  Breadcrumbs,
  Link,
  Typography,
  SxProps,
  Theme,
} from "@mui/material";
import { NavigateNext } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useBreadcrumbs from "../../hooks/useBreadcrumbs";

type PageBreadcrumbsProps = {
  sx?: SxProps<Theme>;
};

// "You are here" trail shown at the top-left of every page, e.g.
// Campaigns › BlackFriday 2025 › Edit. Every crumb but the last one
// is a real link, so it doubles as a way back up the hierarchy. The trail
// itself is derived from the URL in useBreadcrumbs.
// Render it outside PageContainer: that container is centered, so a trail
// inside it would drift away from the left edge on wide screens.
const PageBreadcrumbs: React.FC<PageBreadcrumbsProps> = ({ sx }) => {
  const { t } = useTranslation();
  const crumbs = useBreadcrumbs();

  if (crumbs.length === 0) return null;

  return (
    <Box sx={{ mb: 2, pl: 1, ...sx }}>
      <Breadcrumbs
        aria-label={t("breadcrumbs.ariaLabel", "Breadcrumb")}
        separator={<NavigateNext sx={{ fontSize: 18 }} />}
        sx={{
          "& .MuiBreadcrumbs-separator": {
            mx: 0.5,
            color: "text.disabled",
          },
          "& .MuiBreadcrumbs-ol": { flexWrap: "wrap", rowGap: 0.5 },
        }}
      >
        {crumbs.map((crumb, index) => {
          const isLast = index === crumbs.length - 1;

          if (isLast || !crumb.to) {
            return (
              <Typography
                key={`${crumb.label}-${index}`}
                component="span"
                aria-current={isLast ? "page" : undefined}
                title={crumb.label}
                sx={{
                  fontSize: "0.9375rem",
                  fontWeight: 600,
                  color: "text.primary",
                  maxWidth: 320,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {crumb.label}
              </Typography>
            );
          }

          return (
            <Link
              key={`${crumb.label}-${index}`}
              component={RouterLink}
              to={crumb.to}
              underline="none"
              title={crumb.label}
              sx={{
                fontSize: "0.9375rem",
                fontWeight: 500,
                color: "text.secondary",
                maxWidth: 260,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                px: 0.75,
                py: 0.25,
                // Negative margin cancels the padding above so the crumb's
                // hitbox grows without shifting the trail's layout.
                mx: -0.75,
                my: -0.25,
                borderRadius: 1.5,
                border: "1px solid transparent",
                transition: (theme) =>
                  theme.transitions.create(
                    ["background-color", "border-color", "color"],
                    { duration: theme.transitions.duration.shortest }
                  ),
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: "action.hover",
                  borderColor: "divider",
                },
              }}
            >
              {crumb.label}
            </Link>
          );
        })}
      </Breadcrumbs>
    </Box>
  );
};

export default PageBreadcrumbs;
